import { Fragment, useEffect, useState } from "react";

import type { Article, Library } from "../help/parse";
import { isScheduler, referencedIds, search } from "../help/parse";
import { useHelp } from "../help/load";
import { navigate } from "../router";
import { ArticleBody, ArticleLink, splitRelated } from "./HelpArticle";

/**
 * Help: every article, one article, or the articles for one screen (router.ts, the `help`
 * route). Read-only, and the same for everybody signed in: an article for PMs is not secret,
 * and somebody reading ahead of a new role is the help working.
 */
export function Help({ id, screen }: { id: string | null; screen: string | null }) {
  const help = useHelp();

  // One article for the screen: go straight to it, replacing /help/for/<screen> so Back
  // returns to the screen the ? was pressed on, not to this hop.
  const only = help && help !== "failed" && screen ? help.library.forScreen(screen) : [];
  const single = only.length === 1 ? only[0] : undefined;
  useEffect(() => {
    if (single) navigate({ kind: "help", id: single.id, screen: null }, true);
  }, [single]);

  if (help === "failed") {
    return (
      <div className="card">
        <h2>Help could not be loaded</h2>
        <p className="muted">Reload the page to try again.</p>
      </div>
    );
  }
  if (!help || single) return <p className="muted">Loading…</p>;

  const lib = help.library;
  const article = id ? lib.byId.get(id) : undefined;
  if (article) return <ArticlePage article={article} lib={lib} sizes={help.imageSizes} />;

  let note: string | null = null;
  if (id) note = `There is no article ${id} yet. Every article there is is below.`;
  else if (screen && only.length === 0) note = "No article covers that screen yet. Every article there is is below.";
  return <HelpIndex lib={lib} forScreen={screen && only.length > 1 ? only : null} note={note} />;
}

// ── Grouping ────────────────────────────────────────────────────────────────

interface Group {
  key: string;
  label: string;
  parts: { label: string | null; articles: Article[] }[];
}

/** Everyone (Fieldpath, then the scheduler), PMs and task managers, Admins and HR. */
function groups(articles: readonly Article[]): Group[] {
  const of = (audience: Article["audience"]) => articles.filter((a) => a.audience === audience);
  const everyone = of("everyone");
  return [
    {
      key: "everyone",
      label: "Everyone",
      parts: [
        { label: "Fieldpath", articles: everyone.filter((a) => !isScheduler(a)) },
        { label: "Resource scheduler", articles: everyone.filter(isScheduler) },
      ],
    },
    { key: "pm", label: "PMs and task managers", parts: [{ label: null, articles: of("pm") }] },
    { key: "admin", label: "Admins and HR", parts: [{ label: null, articles: of("admin") }] },
  ];
}

const groupLabel = (a: Article): string =>
  a.audience === "pm"
    ? "PMs and task managers"
    : a.audience === "admin"
      ? "Admins and HR"
      : isScheduler(a)
        ? "Everyone · Resource scheduler"
        : "Everyone · Fieldpath";

// ── The search box, shared ──────────────────────────────────────────────────

/**
 * Kept for the session rather than per screen, so searching, opening a result and pressing Back
 * finds the search as it was left.
 */
let lastQuery = "";

function useQuery(): [string, (q: string) => void] {
  const [q, setQ] = useState(lastQuery);
  return [
    q,
    (next: string) => {
      lastQuery = next;
      setQ(next);
    },
  ];
}

function SearchBox({
  q,
  setQ,
  autoFocus,
  short,
}: {
  q: string;
  setQ: (q: string) => void;
  autoFocus?: boolean;
  /** In the narrow column beside an article, where the long prompt is cut off. */
  short?: boolean;
}) {
  return (
    <input
      type="search"
      className="help-search"
      value={q}
      placeholder={short ? "Search help" : "Search help: a word on a screen, or what you are trying to do"}
      aria-label="Search help"
      autoFocus={autoFocus}
      onChange={(e) => setQ(e.target.value)}
    />
  );
}

function Tag({ a }: { a: Article }) {
  return <span className="help-id">{a.id}</span>;
}

// ── The index ───────────────────────────────────────────────────────────────

function HelpIndex({ lib, forScreen, note }: { lib: Library; forScreen: Article[] | null; note: string | null }) {
  const [q, setQ] = useQuery();
  const hits = search(lib, q);
  const searching = q.trim() !== "";

  return (
    <div className="help">
      <div className="count-line">
        {searching ? `${hits.length} OF ${lib.articles.length} ARTICLES` : `${lib.articles.length} ARTICLES`}
      </div>
      <h1 className="page-h1">Help</h1>
      <div className="help-searchbar">
        <SearchBox q={q} setQ={setQ} autoFocus={!forScreen} />
      </div>
      {note ? <p className="muted help-note-line">{note}</p> : null}

      {forScreen && !searching ? (
        <section className="help-forscreen">
          <div className="section-label">
            <span>For the screen you were on</span>
            <span className="count">{forScreen.length}</span>
          </div>
          {forScreen.map((a) => (
            <ArticleRow key={a.id} a={a} showGroup />
          ))}
        </section>
      ) : null}

      {searching ? (
        <section className="help-results">
          {hits.length === 0 ? (
            <div className="empty">No article mentions “{q.trim()}”. Try fewer words, or the word the screen uses.</div>
          ) : (
            hits.map(({ article: a, snippet }) => (
              <div key={a.id} className="help-row">
                <Tag a={a} />
                <div className="help-row-main">
                  <ArticleLink id={a.id} className="help-row-title">
                    {a.title}
                  </ArticleLink>
                  <span className="help-row-group">{groupLabel(a)}</span>
                  {snippet ? (
                    <div className="help-row-lede">
                      {snippet.before}
                      <mark>{snippet.match}</mark>
                      {snippet.after}
                    </div>
                  ) : null}
                </div>
              </div>
            ))
          )}
        </section>
      ) : (
        <div className="help-groups">
          {groups(lib.articles).map((g) => (
            <section key={g.key} className="help-group">
              <div className="section-label">
                <span>{g.label}</span>
                <span className="count">{g.parts.reduce((n, p) => n + p.articles.length, 0)}</span>
              </div>
              {g.parts.map((p, i) => (
                <Fragment key={i}>
                  {p.label ? <div className="help-part">{p.label}</div> : null}
                  {p.articles.length === 0 ? (
                    <p className="muted help-none">Articles to come.</p>
                  ) : (
                    p.articles.map((a) => <ArticleRow key={a.id} a={a} />)
                  )}
                </Fragment>
              ))}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function ArticleRow({ a, showGroup }: { a: Article; showGroup?: boolean }) {
  return (
    <div className="help-row">
      <Tag a={a} />
      <div className="help-row-main">
        <ArticleLink id={a.id} className="help-row-title">
          {a.title}
        </ArticleLink>
        {showGroup ? <span className="help-row-group">{groupLabel(a)}</span> : null}
        {a.lede ? <div className="help-row-lede">{a.lede}</div> : null}
      </div>
    </div>
  );
}

// ── One article ─────────────────────────────────────────────────────────────

function ArticlePage({
  article,
  lib,
  sizes,
}: {
  article: Article;
  lib: Library;
  sizes: Readonly<Record<string, readonly [number, number]>>;
}) {
  const [q, setQ] = useQuery();
  const { body, relatedBlocks } = splitRelated(article.blocks);
  // The front matter's list first, then anything the Related section links that it left out.
  const relatedIds = [...article.related];
  for (const extra of referencedIds(relatedBlocks)) if (!relatedIds.includes(extra)) relatedIds.push(extra);
  const related = relatedIds.filter((r) => r !== article.id);

  // A new article starts at its top, not wherever the last one was scrolled to. A block body on
  // purpose: Edge's scrollTo now returns a promise, and an arrow returning it hands React that
  // promise as the effect's cleanup, which throws "destroy is not a function" and blanks the page.
  // Unless the address names a place in it -- `/help/E23#v1-1`, where a What's new notice
  // sends people (F149) -- which is scrolled to instead.
  useEffect(() => {
    const anchor = window.location.hash.slice(1);
    const target = anchor ? document.getElementById(anchor) : null;
    if (target) target.scrollIntoView();
    else window.scrollTo(0, 0);
  }, [article.id]);

  return (
    <div className="help help-reading">
      <aside className="help-side">
        <SearchBox q={q} setQ={setQ} short />
        <SideList lib={lib} q={q} current={article.id} />
      </aside>

      <article className="help-article">
        <nav className="crumbs" aria-label="Where this is">
          <a
            className="linkish"
            href="/help"
            onClick={(e) => {
              if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
              e.preventDefault();
              navigate({ kind: "help", id: null, screen: null });
            }}
          >
            Help
          </a>
          <span className="sep">›</span>
          <span>{groupLabel(article)}</span>
        </nav>
        <h1 className="help-title">
          <span className="help-id head">{article.id}</span>
          {article.title}
        </h1>

        <ArticleBody blocks={body} lib={lib} sizes={sizes} />

        {related.length > 0 ? (
          <div className="help-related">
            <div className="section-label">
              <span>Related</span>
            </div>
            <div className="help-related-row">
              {related.map((r) => {
                const a = lib.byId.get(r);
                return a ? (
                  <ArticleLink key={r} id={r} className="help-related-link">
                    <span className="help-id">{a.id}</span>
                    {a.title}
                  </ArticleLink>
                ) : (
                  <span key={r} className="help-related-link missing" title="Not written yet">
                    <span className="help-id">{r}</span>
                    To come
                  </span>
                );
              })}
            </div>
          </div>
        ) : null}
      </article>
    </div>
  );
}

/** Every article in a column beside the one being read, narrowed by the search box. */
function SideList({ lib, q, current }: { lib: Library; q: string; current: string }) {
  const searching = q.trim() !== "";
  if (searching) {
    const hits = search(lib, q);
    return (
      <nav className="help-side-list" aria-label="Search results">
        <div className="help-part">
          {hits.length} {hits.length === 1 ? "match" : "matches"}
        </div>
        {hits.map(({ article: a }) => (
          <SideItem key={a.id} a={a} current={current} />
        ))}
      </nav>
    );
  }
  return (
    <nav className="help-side-list" aria-label="All help">
      {groups(lib.articles).map((g) =>
        g.parts.map((p, i) =>
          p.articles.length === 0 ? null : (
            <Fragment key={`${g.key}-${i}`}>
              <div className="help-part">{p.label ? `${g.label} · ${p.label}` : g.label}</div>
              {p.articles.map((a) => (
                <SideItem key={a.id} a={a} current={current} />
              ))}
            </Fragment>
          ),
        ),
      )}
    </nav>
  );
}

function SideItem({ a, current }: { a: Article; current: string }) {
  return (
    <ArticleLink id={a.id} className={a.id === current ? "help-side-item on" : "help-side-item"}>
      <span className="help-side-id">{a.id}</span>
      <span>{a.title}</span>
    </ArticleLink>
  );
}

